import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompanyService } from '@app/features/companies/company.service';
import {
  SchedulesForm,
  SchedulesFormValue,
} from '@app/features/contracts/embauche-dialog/embauche-tabs-form/schedules-form/schedules-form';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { SaveButton } from '@app/shared/buttons/save-button/save-button';
import { ColorPickerDirective } from 'ngx-color-picker';
import { BehaviorSubject, catchError, map, of } from 'rxjs';
import { PostalAddressForm } from '../../../shared/forms/postal-address-form/postal-address-form';
import { CompanyLogoService } from '../company-logo.service';
import { Company } from '../company.model';
import { LegalInformationForm } from '../legal-information-form/legal-information-form';
import { PostalAddressFormGroup } from '../postal-adress.model';

export interface CompanyDialogData {
  companyId: string;
}

@Component({
  selector: 'app-company-dialog',
  imports: [
    CommonModule,
    SchedulesForm,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    SaveButton,
    LegalInformationForm,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    ColorPickerDirective,
    MatIconModule,
    MatTooltipModule,
    PostalAddressForm,
  ],
  templateUrl: './company-dialog.html',
  styleUrls: ['./company-dialog.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyDialog implements OnInit, OnDestroy, AfterViewInit {
  readonly cdr = inject(ChangeDetectorRef);
  readonly dialogRef = inject(MatDialogRef<CompanyDialog>);
  readonly data = inject<CompanyDialogData | null>(MAT_DIALOG_DATA);
  readonly companyService = inject(CompanyService);
  readonly companyLogoService = inject(CompanyLogoService);
  company = this.data ? this.companyService.findById(this.data.companyId) : undefined;
  readonly hasCompanyLogo$ = new BehaviorSubject<boolean>(false);
  readonly authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);

  initSchedulesFormValue: SchedulesFormValue | undefined = this.data
    ? {
        collectiveSchedules: false,
        schedules: this.company!.collectiveSchedules,
      }
    : undefined;
  color: string = this.company?.companyColor || 'white';
  companyNameControl = new FormControl(this.company?.name || '', {
    nonNullable: true,
    validators: [Validators.required],
  });
  phoneNumberControl = new FormControl(this.company?.phoneNumber || '', {
    nonNullable: true,
    validators: [Validators.required],
  });
  emailControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.email],
  });
  vanexNetworkFolderNameControl = new FormControl(this.company?.vanexNetworkFolderName ?? '', {
    nonNullable: true,
  });
  retraiteComplementaireForm: PostalAddressFormGroup = new FormGroup({
    name: new FormControl(this.company?.retraiteComplementaire.name || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    address: new FormControl(this.company?.retraiteComplementaire.address || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    additionalAddress: new FormControl(this.company?.retraiteComplementaire.additionalAddress, {
      nonNullable: true,
    }),
    postalCode: new FormControl(this.company?.retraiteComplementaire.postalCode || '', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^\d{5}$/)],
    }),
    city: new FormControl(this.company?.retraiteComplementaire.city || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });
  prevoyanceForm: PostalAddressFormGroup = new FormGroup({
    name: new FormControl(this.company?.prevoyance.name || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    additionalAddress: new FormControl(this.company?.prevoyance.additionalAddress, {
      nonNullable: true,
    }),
    address: new FormControl(this.company?.prevoyance.address || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    postalCode: new FormControl(this.company?.prevoyance.postalCode || '', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^\d{5}$/)],
    }),
    city: new FormControl(this.company?.prevoyance.city || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  toggleColorPicker = false;
  selectedLogoPreviewUrl: string | null = null;
  isReadOnly = false;

  @ViewChild(SchedulesForm) schedulesForm?: SchedulesForm;
  @ViewChild(LegalInformationForm) legalInformationForm?: LegalInformationForm;
  @ViewChild(SaveButton) saveButton!: SaveButton;
  @ViewChild('logoFileInput') logoFileInput?: ElementRef<HTMLInputElement>;

  get companyName(): string {
    return this.company?.name ?? this.companyNameControl.value.trim();
  }

  get isFormValid(): boolean {
    return (
      this.companyName?.length > 0 &&
      (this.schedulesForm?.form.valid ?? false) &&
      (this.legalInformationForm?.form.valid ?? false) &&
      this.phoneNumberControl.valid &&
      this.emailControl.valid &&
      this.retraiteComplementaireForm.valid &&
      this.prevoyanceForm.valid
    );
  }

  ngOnInit() {
    if (this.company) {
      this.emailControl.setValue(this.company.email);
      this.phoneNumberControl.setValue(this.company.phoneNumber);
      this.companyLogoService
        .findById(this.company.id)
        .pipe(
          map(() => true),
          catchError(() => of(false)),
        )
        .subscribe((hasLogo) => {
          this.hasCompanyLogo$.next(hasLogo);
          this.cdr.markForCheck();
        });
    }
    this.isReadOnly = !this.authenticatedUserService.hasAnyRole(['directeur-rh', 'responsable-rh']);
  }

  ngAfterViewInit(): void {
    this.initEnability();
  }

  initEnability() {
    if (this.isReadOnly) {
      this.legalInformationForm?.form.disable();
      this.companyNameControl.disable();
      this.phoneNumberControl.disable();
      this.emailControl.disable();
      this.vanexNetworkFolderNameControl.disable();
      this.retraiteComplementaireForm.disable();
      this.prevoyanceForm.disable();
      this.schedulesForm?.form.disable();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    this.clearSelectedLogoPreview();
  }

  onClickChangeLogo() {
    this.logoFileInput?.nativeElement.click();
  }

  onLogoFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const selectedFile = input.files?.[0] ?? null;

    if (!selectedFile) {
      return;
    }

    this.clearSelectedLogoPreview();
    this.selectedLogoPreviewUrl = URL.createObjectURL(selectedFile);

    this.companyLogoService.uploadLogo(this.company!.id, selectedFile).subscribe({
      next: () => {
        this.hasCompanyLogo$.next(true);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error uploading logo:', error);
      },
    });
    input.value = '';
  }

  private clearSelectedLogoPreview() {
    if (this.selectedLogoPreviewUrl) {
      URL.revokeObjectURL(this.selectedLogoPreviewUrl);
      this.selectedLogoPreviewUrl = null;
    }
  }

  onSave() {
    if (!this.isFormValid) {
      return;
    }
    const companySchedules = this.schedulesForm!.form.getRawValue().schedules;
    const legalInformation = this.legalInformationForm!.form.getRawValue();
    const retraiteComplementaire = this.retraiteComplementaireForm.getRawValue();
    const prevoyance = this.prevoyanceForm.getRawValue();

    const companyUpdate: Company = {
      ...this.company!,
      companyColor: this.color,
      name: this.companyName,
      collectiveSchedules: companySchedules,
      legalInformation,
      retraiteComplementaire,
      prevoyance,
      // Un champ vidé s'envoie vide, non comme une chaîne d'espaces : l'application de bureau ne
      // range pas les contrats d'une société sans dossier réseau.
      vanexNetworkFolderName: this.vanexNetworkFolderNameControl.value.trim() || null,
    };
    if (this.data) {
      this.update(companyUpdate);
    } else {
      this.create(companyUpdate);
    }
  }

  private create(company: Company) {
    this.saveButton.startSaving();
    this.companyService.create(company).subscribe({
      next: (createdCompany) => {
        this.saveButton.stopSaving(true);
        this.dialogRef.close(createdCompany);
      },
      error: (error) => {
        console.error('Error creating company:', error);
        this.saveButton.stopSaving(false);
      },
    });
  }

  private update(company: Company) {
    this.saveButton.startSaving();
    this.companyService.update(company).subscribe({
      next: (updatedCompany) => {
        this.saveButton.stopSaving(true);
        this.dialogRef.close(updatedCompany);
      },
      error: (error) => {
        console.error('Error updating company:', error);
        this.saveButton.stopSaving(false);
      },
    });
  }
}
